"use client";

import { FormEvent, useState } from "react";
import { useRouter } from "next/navigation";

import { createClient } from "@/lib/supabase/client";
import { resolveStudentLogin } from "./actions";

type LoginMode = "student" | "staff";

export default function LoginPage() {
  const router = useRouter();
  const supabase = createClient();

  const [mode, setMode] = useState<LoginMode>("student");

  const [schoolCode, setSchoolCode] = useState("");
  const [username, setUsername] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleStaffLogin(
    event: FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    setLoading(true);
    setError("");

    const { error } =
      await supabase.auth.signInWithPassword({
        email,
        password,
      });

    if (error) {
      setError("Feil e-postadresse eller passord.");
      setLoading(false);
      return;
    }

    router.push("/dashboard");
    router.refresh();
  }

  async function handleStudentLogin(
    event: FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    setLoading(true);
    setError("");

    const formData = new FormData();

    formData.set("school_code", schoolCode);
    formData.set("username", username);

    const result = await resolveStudentLogin(
      {
        error: null,
        email: null,
      },
      formData
    );

    if (result.error || !result.email) {
      setError(
        result.error ??
          "Kunne ikke finne elevkontoen."
      );

      setLoading(false);
      return;
    }

    const { error: loginError } =
      await supabase.auth.signInWithPassword({
        email: result.email,
        password,
      });

    if (loginError) {
      setError(
        "Feil skolekode, brukernavn eller passord."
      );

      setLoading(false);
      return;
    }

    router.push("/dashboard");
    router.refresh();
  }

  function switchMode(newMode: LoginMode) {
    setMode(newMode);
    setError("");
    setPassword("");
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-950 p-6">

      <div className="w-full max-w-md">

        <div className="mb-8 text-center">

          <div className="text-sm font-semibold uppercase tracking-[0.4em] text-cyan-400">
            TypeQuest
          </div>

          <h1 className="mt-4 text-4xl font-bold text-white">
            Klar for neste quest?
          </h1>

          <p className="mt-3 text-slate-400">
            Logg inn og fortsett reisen.
          </p>

        </div>


        <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">

          <div className="mb-7 grid grid-cols-2 rounded-xl bg-slate-950 p-1">

            <button
              type="button"
              onClick={() => switchMode("student")}
              className={
                mode === "student"
                  ? "rounded-lg bg-cyan-400 px-4 py-3 text-sm font-bold text-slate-950"
                  : "rounded-lg px-4 py-3 text-sm font-semibold text-slate-400 hover:text-white"
              }
            >
              Elev
            </button>

            <button
              type="button"
              onClick={() => switchMode("staff")}
              className={
                mode === "staff"
                  ? "rounded-lg bg-cyan-400 px-4 py-3 text-sm font-bold text-slate-950"
                  : "rounded-lg px-4 py-3 text-sm font-semibold text-slate-400 hover:text-white"
              }
            >
              Ansatt
            </button>

          </div>


          {mode === "student" ? (

            <form
              onSubmit={handleStudentLogin}
              className="space-y-5"
            >

              <div>

                <label
                  htmlFor="school_code"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Skolekode
                </label>

                <input
                  id="school_code"
                  type="text"
                  required
                  value={schoolCode}
                  onChange={(event) =>
                    setSchoolCode(
                      event.target.value.toUpperCase()
                    )
                  }
                  placeholder="TEST01"
                  autoComplete="off"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 uppercase text-white outline-none focus:border-cyan-400"
                />

              </div>


              <div>

                <label
                  htmlFor="username"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Brukernavn
                </label>

                <input
                  id="username"
                  type="text"
                  required
                  value={username}
                  onChange={(event) =>
                    setUsername(event.target.value)
                  }
                  placeholder="emma.h"
                  autoComplete="username"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-400"
                />

              </div>


              <div>

                <label
                  htmlFor="student_password"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Passord
                </label>

                <input
                  id="student_password"
                  type="password"
                  required
                  value={password}
                  onChange={(event) =>
                    setPassword(event.target.value)
                  }
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-400"
                />

              </div>


              {error && (
                <div className="rounded-xl border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
                  {error}
                </div>
              )}


              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-cyan-400 px-4 py-3 font-bold text-slate-950 transition hover:bg-cyan-300 disabled:opacity-60"
              >
                {loading
                  ? "Logger inn..."
                  : "Start eventyret"}
              </button>

            </form>

          ) : (

            <form
              onSubmit={handleStaffLogin}
              className="space-y-5"
            >

              <div>

                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  E-post
                </label>

                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(event) =>
                    setEmail(event.target.value)
                  }
                  autoComplete="email"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-400"
                />

              </div>


              <div>

                <label
                  htmlFor="staff_password"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Passord
                </label>

                <input
                  id="staff_password"
                  type="password"
                  required
                  value={password}
                  onChange={(event) =>
                    setPassword(event.target.value)
                  }
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-400"
                />

              </div>


              {error && (
                <div className="rounded-xl border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
                  {error}
                </div>
              )}


              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-cyan-400 px-4 py-3 font-bold text-slate-950 transition hover:bg-cyan-300 disabled:opacity-60"
              >
                {loading ? "Logger inn..." : "Logg inn"}
              </button>

            </form>

          )}

        </div>

        <p className="mt-6 text-center text-xs text-slate-600">
          TypeQuest v0.1
        </p>

      </div>

    </main>
  );
}
